// src/hooks/macro/useMacroOverview.ts

import { useQuery } from '@tanstack/react-query';
import { macroOverviewQueryOptions } from '@/services/queries/macro/macroOverviewQueries';
import type { MacroOverview } from '@/types/dashboards/macro';

export interface UseMacroOverviewOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export interface UseMacroOverviewResult {
  data: MacroOverview[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * React hook for fetching macro overview data
 * @param options - Configuration options for the data fetch
 * @returns Object containing data, loading state, error, and refetch function
 */
export const useMacroOverview = (options: UseMacroOverviewOptions = {}): UseMacroOverviewResult => {
  const { enabled = true, refetchInterval } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
    isSuccess,
    isError,
  } = useQuery({
    ...macroOverviewQueryOptions(),
    enabled,
    ...(refetchInterval !== undefined && { refetchInterval }),
  });

  return {
    data: data?.data,
    isLoading,
    error: error as Error | null,
    refetch,
    isSuccess,
    isError,
  };
};

export default useMacroOverview;
